import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { isValidCpf, maskCpf, maskPhone } from '../../../../../domain';
import { CompanyUsersApi } from '../company-users.api';
import { CompanyUsersKeys } from '../company-users.key';
import { ProfessionalTypeChoice } from '../professional-type-choice';
import { CompanyUserFormKeys } from './company-user-form.key';
import { CompanyCandidateScope, CompanyUserFormScope, ProfessionalTypeScope } from './company-user-form.scope';
const LOG = Logger.get('Mormaii.CompanyUserFormPresenter');
// @Inject
const api = CompanyUsersApi.INSTANCE;
/**
 * Quanto o campo de e-mail espera parar de ser digitado antes de procurar.
 *
 * 400ms, e nao os 250 do seletor de empresa: um e-mail e mais longo que um nome de
 * empresa, e quem o digita costuma fazer uma pausa no `@`.
 */
const LOOKUP_DEBOUNCE_MS = 400;
/**
 * O cadastro de um usuario da empresa - administrador ou profissional.
 *
 * Um dialogo aberto E um lugar onde o leitor esta, entao ele e um place:
 * `#/hub/company-users/form?cb=1` abre o formulario de profissional, um reload o
 * mantem aberto e o Voltar o fecha, sem ninguem escrever tratamento de Escape.
 *
 * O que esta DIGITADO nao vai para a URL, e isso e deliberado: um rascunho nao e um
 * lugar, e um endereco que muda a cada tecla encheria o historico.
 */
export class CompanyUserFormPresenter extends CubePresenter {
    constructor(app) {
        super(app, new CompanyUserFormScope());
        this.dialogSlot = NOOP_VOID;
        this.role = 'admin';
        /** A profissao escolhida. O id vai no corpo; o resto e so a lista na tela. */
        this.choice = new ProfessionalTypeChoice();
        /** Qual procura ainda pode falar - duas respostas podem cruzar. */
        this.lookupToken = 0;
        /** Qual busca de candidatos ainda pode falar. */
        this.candidateToken = 0;
    }
    async applyParameters(intent, initialization) {
        const keys = new CompanyUserFormKeys(this.app, intent);
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.area = keys.area;
        if (initialization) {
            this.scope.onFirstNameChanged = this.handleChanged.bind(this, 'firstName');
            this.scope.onLastNameChanged = this.handleChanged.bind(this, 'lastName');
            this.scope.onEmailChanged = this.handleEmailChanged.bind(this);
            this.scope.onCpfChanged = this.handleCpfChanged.bind(this);
            this.scope.onPhoneChanged = this.handlePhoneChanged.bind(this);
            this.scope.onRegistrationChanged = this.handleChanged.bind(this, 'registrationNumber');
            this.scope.onUfChanged = this.handleUfChanged.bind(this);
            this.scope.onClearType = this.action(this.onClearType);
            this.scope.onModeChanged = this.handleModeChanged.bind(this);
            this.scope.onCandidateTermChanged = this.handleCandidateTermChanged.bind(this);
            this.scope.onForgetCandidate = this.action(this.onForgetCandidate);
            this.scope.onSave = this.action(this.onSave);
            this.scope.onClose = this.action(this.onClose);
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.dialogSlot = keys.dialogSlot;
        this.role = keys.role;
        /*
         * A MESMA empresa da area atras do dialogo - ver `companyOnArrival`.
         *
         * Era so `keys.companyId`, e para quem escolhe a empresa no CABECALHO o endereco
         * da area nao a carrega (`companyIdInAddress`): toda chamada do dialogo saia sem
         * empresa, e o servidor respondia 400 "Voce administra mais de uma empresa.
         * Informe qual." a quem administra duas (31/10/2026).
         */
        this.companyId = keys.owner?.companyOnArrival(keys.companyId);
        this.scope.professional = this.role === 'profissional';
        this.showTitle();
        this.dialogSlot(this.scope);
        // As profissoes so sao pedidas quando ha onde mostra-las - o cadastro de
        // administrador nao tem conselho, e pagaria uma consulta por nada.
        if (this.scope.professional && this.choice.professions.length === 0) {
            await this.loadTypes();
        }
        return true;
    }
    /**
     * O PAPEL vai para o endereco; o que esta digitado, nao.
     *
     * Sem isto o dialogo abria certo e o F5 o trazia de volta como cadastro de
     * administrador - o mesmo endereco querendo dizer duas coisas.
     */
    publishParameters(intent) {
        const keys = new CompanyUserFormKeys(this.app, intent);
        keys.role = this.role;
    }
    /**
     * O titulo conta o que esta acontecendo - e sao duas coisas diferentes.
     *
     * "Cadastrar profissional" numa tela que vai apenas acrescentar uma empresa a
     * quem ja existe promete uma pessoa nova que nao vai nascer.
     */
    showTitle() {
        const papel = this.scope.professional ? 'profissional' : 'administrador';
        this.scope.title =
            this.scope.mode === 'vinculo' ? `Vincular ${papel} que já está na plataforma` : `Cadastrar ${papel}`;
    }
    /**
     * Troca entre CADASTRAR alguem e VINCULAR quem ja existe.
     *
     * O que foi digitado de um lado nao atravessa para o outro: sao perguntas
     * diferentes, e um nome esquecido no formulario apareceria depois num cadastro
     * que a pessoa achou que tinha abandonado.
     */
    handleModeChanged(mode) {
        this.scope.mode = mode === 'vinculo' ? 'vinculo' : 'cadastro';
        this.scope.formError = undefined;
        this.forgetExisting();
        this.showTitle();
        if (this.scope.mode === 'vinculo') {
            this.scope.email = '';
            void this.loadCandidates(this.scope.candidateTerm);
        }
    }
    handleCandidateTermChanged(value) {
        this.scope.candidateTerm = value;
        if (this.candidateTimer) {
            clearTimeout(this.candidateTimer);
        }
        this.candidateTimer = setTimeout(() => {
            void this.loadCandidates(value);
        }, LOOKUP_DEBOUNCE_MS);
    }
    /**
     * Quem ja esta na plataforma e da para vincular.
     *
     * Sem termo a lista ja vem preenchida com a gente das outras empresas de quem
     * pede - e o caso que motivou isto tudo, e faze-lo digitar para descobrir que a
     * pessoa esta ali seria esconder a resposta atras de uma pergunta.
     */
    async loadCandidates(term) {
        const token = ++this.candidateToken;
        this.scope.candidatesLoading = true;
        try {
            const achados = await api.fetchCandidates(term, this.scope.professional, this.companyId);
            if (token !== this.candidateToken) {
                return;
            }
            this.scope.candidates = achados.map((achado) => this.candidateScopeOf(achado));
        }
        catch (caught) {
            if (token === this.candidateToken) {
                LOG.warn('Could not load the candidates', caught);
                this.scope.candidates = [];
                this.scope.formError = 'Não foi possível buscar quem já está na plataforma.';
            }
        }
        finally {
            if (token === this.candidateToken) {
                this.scope.candidatesLoading = false;
                this.scope.candidatesAsked = true;
            }
        }
    }
    candidateScopeOf(achado) {
        const scope = new CompanyCandidateScope();
        scope.identity = String(achado.userId);
        scope.name = achado.name;
        scope.email = achado.email;
        scope.cpfMascarado = achado.cpfMascarado;
        scope.profissao = achado.professionalTypeName ?? undefined;
        scope.minha = achado.minha;
        scope.onSelect = this.action(this.onChooseCandidate.bind(this, achado));
        scope.update = this.update;
        return scope;
    }
    /**
     * Escolhida a pessoa, a tela vira a MESMA de quando o e-mail a reconhece.
     *
     * Dois caminhos, um estado so: o que muda e como se chega ate aqui - digitando o
     * e-mail ou apontando na lista -, e nao o que acontece depois.
     */
    async onChooseCandidate(achado) {
        this.existingId = achado.userId;
        this.scope.existingName = achado.name;
        this.scope.existingCpf = achado.cpfMascarado;
        this.scope.existingBlock = undefined;
        this.scope.linking = true;
        this.scope.cpf = '';
        this.scope.cpfError = undefined;
        this.scope.formError = undefined;
        /*
         * A profissao vem preenchida com a do cadastro mais recente dela - e uma
         * sugestao, e nao uma resposta: a mesma pessoa pode ser nutricionista num
         * cliente e preparadora fisica em outro, e e por isso que a pergunta fica
         * na tela mesmo ja respondida.
         */
        if (this.scope.professional && achado.professionalTypeId != null) {
            await this.onChooseType(achado.professionalTypeId);
        }
    }
    /** Volta para a lista - escolheu a pessoa errada, ou mudou de ideia. */
    async onForgetCandidate() {
        this.forgetExisting();
        this.scope.cpf = '';
        await this.loadCandidates(this.scope.candidateTerm);
    }
    async loadTypes() {
        try {
            this.choice.receive(await api.fetchProfessionalTypes());
            this.showTypes();
        }
        catch (caught) {
            LOG.warn('Could not load the professional types', caught);
            // A lista de profissoes e opcional no corpo: sem ela o cadastro continua
            // possivel, e derrubar o dialogo por causa dela seria desproporcional.
            this.scope.formError = 'Não foi possível carregar as profissões.';
        }
    }
    /**
     * A lista de profissoes, montada UMA vez.
     *
     * Escolher marca a opcao no lugar em vez de refazer a lista, e isso nao e
     * economia: o seletor le `options` de forma reativa, e trocar o array durante o
     * proprio `onChange` dele realimentava a mudanca - "Maximum call stack size
     * exceeded" com as 55 profissoes da base.
     */
    showTypes() {
        const escolhida = this.choice.chosenProfession;
        const especialidade = this.choice.chosenSpecialty;
        this.scope.types = this.opcoes(this.choice.professions, escolhida?.id, (id) => this.onChooseType(id));
        this.scope.typeSummary = escolhida?.name ?? '';
        this.scope.specialties = this.opcoes(this.choice.specialties, especialidade?.id, (id) => this.onChooseSpecialty(id));
        this.scope.specialtySummary = especialidade?.name ?? '';
        this.scope.regulated = this.choice.regulated;
    }
    /** Uma lista de opcoes do seletor, com a atual marcada. */
    opcoes(tipos, atual, escolher) {
        return tipos.map((type) => {
            const scope = new ProfessionalTypeScope();
            scope.identity = String(type.id);
            scope.label = type.name;
            scope.value = String(type.id);
            scope.current = atual === type.id;
            scope.onSelect = this.action(escolher.bind(this, type.id));
            scope.update = this.update;
            return scope;
        });
    }
    /**
     * Desfaz a escolha.
     *
     * Leva o registro junto: ele so existia por causa do conselho, e um numero de
     * CRM pendurado numa profissao que nao foi escolhida iria para o servidor.
     */
    async onClearType() {
        this.choice.clear();
        this.scope.registrationNumber = '';
        this.scope.uf = '';
        this.showTypes();
    }
    /**
     * Escolher a PROFISSAO derruba a especialidade.
     *
     * Trocar de "Medico" para "Personal trainer" mantendo "Cardiologia" mandaria ao
     * servidor uma especialidade que nao pertence a profissao escolhida.
     */
    async onChooseType(typeId) {
        this.choice.chooseProfession(typeId);
        this.showTypes();
    }
    async onChooseSpecialty(specialtyId) {
        this.choice.chooseSpecialty(specialtyId);
        this.showTypes();
    }
    async onClearSpecialty() {
        this.choice.clearSpecialty();
        this.showTypes();
    }
    /**
     * O e-mail, e a PROCURA por quem ja esta na plataforma.
     *
     * Espera parar de digitar antes de perguntar - a cada tecla seriam vinte consultas
     * para um endereco. O mesmo intervalo do seletor de empresa, e pela mesma razao.
     */
    handleEmailChanged(value) {
        this.scope.email = value;
        this.clearError('email');
        this.forgetExisting();
        if (this.lookupTimer) {
            clearTimeout(this.lookupTimer);
        }
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim())) {
            return;
        }
        this.lookupTimer = setTimeout(() => {
            void this.lookup(value.trim());
        }, LOOKUP_DEBOUNCE_MS);
    }
    async lookup(email) {
        const token = ++this.lookupToken;
        this.scope.looking = true;
        try {
            const achado = await api.lookup(email, this.scope.professional, this.companyId);
            // Quem perdeu a corrida fica calado: digitar rapido dispara varias.
            if (token !== this.lookupToken) {
                return;
            }
            if (!achado) {
                this.forgetExisting();
                return;
            }
            this.scope.existingName = achado.name;
            this.scope.existingCpf = achado.cpfMascarado;
            this.scope.existingBlock = this.blockFor(achado);
            this.scope.linking = !this.scope.existingBlock;
            this.existingId = achado.userId;
        }
        catch (caught) {
            LOG.warn('Could not look the person up', caught);
            // Falhar aqui nao impede cadastrar: o servidor confere de novo ao salvar.
            this.forgetExisting();
        }
        finally {
            if (token === this.lookupToken) {
                this.scope.looking = false;
            }
        }
    }
    /**
     * O que impede vincular esta conta - dito aqui, e nao descoberto ao salvar.
     *
     * As duas recusas existem no servidor e sao as mesmas; a diferenca e o momento.
     * Deixa-las so la faria a pessoa preencher, mandar e tomar um erro - quando a
     * resposta ja estava em maos desde a busca pelo e-mail.
     */
    blockFor(achado) {
        if (achado.jaNestaEmpresa) {
            return this.scope.professional
                ? `${achado.name} já atende esta empresa. Não há o que vincular.`
                : `${achado.name} já administra esta empresa. Não há o que vincular.`;
        }
        // Ser profissional de saude e uma qualidade da PESSOA, e nao um papel que esta
        // empresa concede: o registro de conselho e um so, e nasce com a conta.
        if (this.scope.professional && !achado.profissional) {
            return (`A conta de ${achado.name} não é de um profissional de saúde. ` +
                'Cadastre-a como administrador, ou use outro e-mail.');
        }
        return undefined;
    }
    forgetExisting() {
        this.scope.existingName = undefined;
        this.scope.existingCpf = undefined;
        this.scope.existingBlock = undefined;
        this.scope.linking = false;
        this.existingId = undefined;
    }
    handleChanged(field, value) {
        this.scope[field] = value;
        this.clearError(field);
    }
    handleCpfChanged(value) {
        this.scope.cpf = maskCpf(value);
        this.scope.cpfError = undefined;
    }
    handlePhoneChanged(value) {
        this.scope.phone = maskPhone(value);
        this.scope.phoneError = undefined;
    }
    /** `SC`, e nao `sc`: e como o legado grava, e como o resto do sistema le. */
    handleUfChanged(value) {
        this.scope.uf = value
            .toUpperCase()
            .replace(/[^A-Z]/g, '')
            .slice(0, 2);
    }
    clearError(field) {
        switch (field) {
            case 'firstName':
                this.scope.firstNameError = undefined;
                break;
            case 'lastName':
                this.scope.lastNameError = undefined;
                break;
            case 'email':
                this.scope.emailError = undefined;
                break;
        }
        if (this.scope.formError) {
            this.scope.formError = undefined;
        }
    }
    async onSave() {
        if (!this.validate()) {
            return;
        }
        if (this.scope.linking && this.existingId !== undefined) {
            await this.postLink();
            return;
        }
        const comum = {
            firstName: this.scope.firstName.trim(),
            lastName: this.scope.lastName.trim(),
            email: this.scope.email.trim(),
            cpf: this.scope.cpf.trim(),
            telephoneNumber: this.scope.phone.trim()
        };
        this.scope.saving = true;
        try {
            if (this.scope.professional) {
                await api.addProfessional({
                    ...comum,
                    registrationNumber: this.scope.registrationNumber.trim() || undefined,
                    uf: this.scope.uf.trim() || undefined,
                    professionalTypeId: this.choice.professionalTypeId
                }, this.companyId);
            }
            else {
                await api.addAdmin(comum, this.companyId);
            }
            const papel = this.scope.professional ? 'Profissional' : 'Administrador';
            await this.close();
            this.app.notify(`${papel} cadastrado`, 'A senha temporária foi enviada por e-mail.', 'Continuar', 'user-plus');
        }
        catch (caught) {
            LOG.warn('Could not create the user', caught);
            this.scope.formError =
                caught instanceof Error && caught.message ? caught.message : 'Não foi possível cadastrar.';
        }
        finally {
            this.scope.saving = false;
        }
    }
    /**
     * VINCULA quem ja existe - dois campos, e nao um cadastro.
     *
     * Nada do que e da pessoa viaja: nome, telefone e senha continuam como ela os tem.
     * O CPF vai porque e a prova de identidade, e o servidor o confere contra a conta.
     */
    async postLink() {
        this.scope.saving = true;
        this.scope.formError = undefined;
        try {
            await api.link({
                userId: this.existingId,
                cpf: this.scope.cpf.trim(),
                profissional: this.scope.professional,
                // A profissao vai porque ela e DO VINCULO: sem ela, esta empresa
                // herdaria a que a pessoa exerce na outra.
                professionalTypeId: this.scope.professional ? this.choice.professionalTypeId : undefined
            }, this.companyId);
            const nome = this.scope.existingName ?? 'A pessoa';
            const como = this.scope.professional && this.scope.typeSummary ? ` como ${this.scope.typeSummary.toLowerCase()}` : '';
            await this.close();
            this.app.notify('Vínculo criado', `${nome} passou a fazer parte desta empresa${como}. Continua entrando com a senha que já usa.`, 'Continuar', 'user-check');
        }
        catch (caught) {
            LOG.warn('Could not link the person', caught);
            this.scope.formError =
                caught instanceof Error && caught.message ? caught.message : 'Não foi possível vincular.';
        }
        finally {
            this.scope.saving = false;
        }
    }
    /**
     * Confere antes de mandar, e aponta o campo errado.
     *
     * As mesmas regras do `@Valid` do servidor, repetidas aqui de proposito: sem
     * elas ele responde uma mensagem so, e quem digitou nao descobre QUAL dos cinco
     * campos foi recusado. O CPF vai alem da forma e confere os digitos - o servidor
     * so cobra o formato, e um CPF com a mascara certa e os numeros errados so seria
     * descoberto muito depois.
     */
    validate() {
        this.scope.formError = undefined;
        /*
         * Vinculando, so o CPF e conferido: os campos de pessoa nem aparecem na tela,
         * porque nada do que e da pessoa vai ser tocado.
         */
        if (this.scope.linking) {
            this.scope.cpfError = isValidCpf(this.scope.cpf) ? undefined : 'Informe o CPF desta pessoa.';
            return !this.scope.cpfError;
        }
        this.scope.firstNameError = this.scope.firstName.trim() ? undefined : 'Informe o nome.';
        this.scope.lastNameError = this.scope.lastName.trim() ? undefined : 'Informe o sobrenome.';
        this.scope.emailError = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.scope.email.trim())
            ? undefined
            : 'Informe um e-mail válido.';
        this.scope.cpfError = isValidCpf(this.scope.cpf) ? undefined : 'Informe um CPF válido.';
        this.scope.phoneError = this.scope.phone.trim() ? undefined : 'Informe o telefone.';
        return !(this.scope.firstNameError ||
            this.scope.lastNameError ||
            this.scope.emailError ||
            this.scope.cpfError ||
            this.scope.phoneError);
    }
    /**
     * Cancelar: volta para a area SEM recarregar - nada mudou.
     *
     * Recarregava, e a grade atras piscava a cada cancelamento (08/10/2026).
     */
    async onClose() {
        await CompanyUsersKeys.of(this.app).flip();
    }
    /** Fechar depois de SALVAR: a area recarrega, porque o cadastro mudou a lista. */
    async close() {
        await this.area?.refresh();
        await CompanyUsersKeys.of(this.app).flip();
    }
}
//# sourceMappingURL=company-user-form.presenter.js.map